import React, { useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useGetImportQuery } from '../../../../../api/schedulingApi';
import type { ImportDraft } from '../../../../../types/scheduling';

interface ReviewStepProps {
  importId: string;
  studioId: string;
  onBack: () => void;
  onContinue: () => void;
}

const confidenceColor = (c: string): 'success' | 'warning' | 'default' => {
  if (c === 'high') return 'success';
  if (c === 'medium') return 'warning';
  return 'default';
};

/**
 * Step 2 — show the parsed draft (denormalized placements + entity counts +
 * validation issues) so the owner can sanity-check before committing. Read-only
 * for the MVP; inline editing is a follow-up (the save-draft endpoint is wired).
 */
const ReviewStep: React.FC<ReviewStepProps> = ({
  importId,
  studioId,
  onBack,
  onContinue,
}) => {
  const { data: importRow, isLoading } = useGetImportQuery(
    { id: importId, studioId },
    { skip: !importId }
  );

  const draft = useMemo(
    () => (importRow?.draft || {}) as ImportDraft,
    [importRow]
  );
  const issues = importRow?.error?.issues || [];

  const { rows, columns } = useMemo(() => {
    const classNames = new Map(
      (draft.classes || []).map(c => [c.tmp_id, c.name])
    );
    const roomNames = new Map((draft.rooms || []).map(r => [r.tmp_id, r.name]));
    const teacherNames = new Map(
      (draft.teachers || []).map(t => [t.tmp_id, t.name])
    );
    const gridRows = (draft.placements || []).map((p, idx) => ({
      id: idx,
      className: classNames.get(p.class_tmp_id || '') || '—',
      day: p.day || '—',
      start_time: p.start_time || '—',
      end_time: p.end_time || '—',
      room: roomNames.get(p.room_tmp_id || '') || '—',
      teachers:
        p.teacher_tmp_ids
          .map(tid => teacherNames.get(tid))
          .filter(Boolean)
          .join(', ') || '—',
      confidence: p.confidence,
    }));
    const gridColumns: GridColDef[] = [
      { field: 'className', headerName: 'Class', flex: 1.4, minWidth: 140 },
      { field: 'day', headerName: 'Day', width: 110 },
      { field: 'start_time', headerName: 'Start', width: 80 },
      { field: 'end_time', headerName: 'End', width: 80 },
      { field: 'room', headerName: 'Room', flex: 1, minWidth: 110 },
      { field: 'teachers', headerName: 'Teacher(s)', flex: 1.2, minWidth: 130 },
      {
        field: 'confidence',
        headerName: 'Confidence',
        width: 120,
        renderCell: params => (
          <Chip
            size="small"
            label={params.value}
            color={confidenceColor(params.value)}
            variant={params.value === 'high' ? 'filled' : 'outlined'}
          />
        ),
      },
    ];
    return { rows: gridRows, columns: gridColumns };
  }, [draft]);

  if (isLoading || !importRow) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip label={`${(draft.classes || []).length} classes`} />
        <Chip label={`${(draft.rooms || []).length} rooms`} />
        <Chip label={`${(draft.teachers || []).length} teachers`} />
        <Chip
          color="primary"
          label={`${(draft.placements || []).length} sessions`}
        />
      </Stack>

      {issues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{issues.length} thing(s) to double-check</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {issues.slice(0, 8).map(issue => (
              <li
                key={`${issue.entity}-${issue.tmp_id ?? ''}-${issue.field}-${issue.message}`}
              >
                <Typography variant="body2">
                  {issue.entity}
                  {issue.field ? ` · ${issue.field}` : ''}: {issue.message}
                </Typography>
              </li>
            ))}
            {issues.length > 8 && (
              <li>
                <Typography variant="body2">
                  …and {issues.length - 8} more.
                </Typography>
              </li>
            )}
          </Box>
        </Alert>
      )}

      <Box sx={{ height: 420, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          disableRowSelectionOnClick
          sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
        />
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button onClick={onBack}>Start over</Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={
            importRow.status !== 'ready' ||
            (draft.placements || []).length === 0
          }
        >
          Continue to commit
        </Button>
      </Stack>
    </Box>
  );
};

export default ReviewStep;
